import { useState, type FormEvent } from "react";
import { Mail } from "lucide-react";
import { BUTTON_CLASSES, FOCUS_RING } from "./styles";

type Status = "idle" | "sending" | "sent" | "failed";

// Intakall installs on a computer, so on a phone the useful action is to send
// the link somewhere they will open it later: their inbox.
export function PhoneInstallForm({ refTag }: { refTag: string | null }) {
  const [email, setEmail] = useState("");
  const [updates, setUpdates] = useState(false);
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("/api/install-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ref: refTag ?? "", updates, website }),
      });
      setStatus(res.ok ? "sent" : "failed");
    } catch {
      setStatus("failed");
    }
  }

  if (status === "sent") {
    return (
      <p role="status" className="max-w-sm font-sans text-lg font-semibold text-char">
        Sent. Open it on your computer to install.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="flex w-full max-w-sm flex-col gap-4 text-left">
      <p className="text-center font-sans text-base text-char/80">
        Intakall works on your computer. We'll email you the link.
      </p>

      <label className="flex flex-col gap-1.5 font-sans text-sm font-semibold text-char">
        Your email
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`h-12 rounded-[12px] border border-char bg-white px-4 text-base font-normal text-char ${FOCUS_RING}`}
        />
      </label>

      {/* Bot trap: hidden from people and screen readers, so only bots fill it. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="absolute -left-[9999px] h-px w-px opacity-0"
      />

      <label className="flex items-start gap-3 font-sans text-sm text-char/80">
        <input
          type="checkbox"
          checked={updates}
          onChange={(e) => setUpdates(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
        />
        Also send me occasional updates about Intakall.
      </label>

      <button type="submit" disabled={status === "sending"} className={`${BUTTON_CLASSES} w-full justify-center`}>
        <Mail className="h-4 w-4" aria-hidden="true" />
        {status === "sending" ? "Sending…" : "Email me the link"}
      </button>

      {status === "failed" && (
        <p role="alert" className="text-center font-sans text-sm text-char">
          That didn't work. Try again, or email hello@intakall.io.
        </p>
      )}
    </form>
  );
}
